import { useCallback, useEffect, useRef, useState } from "react";
import { ChevronLeft, MessageCircle, X } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { useMessaging } from "../../context/MessagingContext";
import ConversationList from "./ConversationList";
import MessageList from "./MessageList";
import MessageInput from "./MessageInput";
import {
  formatThreadTimestamp,
  getConversationDisplayName,
  getConversationInitials,
} from "./messagingUi";

function SellerAvatar({ conversation, size = "sm" }) {
  const dim = size === "sm" ? "w-7 h-7 text-[10px]" : "w-9 h-9 text-xs";

  return (
    <div
      className={`${dim} shrink-0 overflow-hidden rounded-full bg-neutral-200 font-bold text-neutral-500 flex items-center justify-center dark:bg-neutral-700`}
    >
      {getConversationInitials(conversation, "SHOPPER")}
    </div>
  );
}

export default function ChatWidget() {
  const { isAuthenticated } = useAuth();
  const {
    conversations,
    activeConversationId,
    activeConversation,
    messages,
    isWidgetOpen,
    unreadTotal,
    error,
    loadingConvos,
    loadingMessages,
    toggleWidget,
    closeWidget,
    selectConversation,
    searchConversations,
    sendMessage,
    clearError,
    getConversationUnreadCount,
  } = useMessaging();

  const [mobileView, setMobileView] = useState("list");
  const [sending, setSending] = useState(false);
  const [interactionError, setInteractionError] = useState("");
  const panelRef = useRef(null);

  useEffect(() => {
    if (activeConversationId) setMobileView("chat");
  }, [activeConversationId]);

  useEffect(() => {
    if (!isWidgetOpen) {
      setMobileView(activeConversationId ? "chat" : "list");
    } else if (!activeConversationId) {
      setMobileView("list");
    }
  }, [activeConversationId, isWidgetOpen]);

  useEffect(() => {
    if (!error) return;
    setInteractionError(error);
  }, [error]);

  useEffect(() => {
    if (!isWidgetOpen) return;

    const handle = (event) => {
      const button = document.getElementById("fcw-toggle-btn");
      if (button?.contains(event.target)) return;
      if (panelRef.current && !panelRef.current.contains(event.target)) {
        closeWidget();
      }
    };

    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, [isWidgetOpen, closeWidget]);

  useEffect(() => {
    if (!isWidgetOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isWidgetOpen]);

  const handleSend = useCallback(
    async (text, file) => {
      if (!activeConversation || sending || (!text && !file)) return false;
      clearError();
      setInteractionError("");
      setSending(true);
      try {
        return await sendMessage(activeConversation.id, text || "", file ?? null);
      } finally {
        setSending(false);
      }
    },
    [activeConversation, clearError, sending, sendMessage]
  );

  const handleSelectConversation = useCallback(
    (conversationId) => {
      clearError();
      setInteractionError("");
      selectConversation(conversationId);
      setMobileView("chat");
    },
    [clearError, selectConversation]
  );

  if (!isAuthenticated) return null;

  return (
    <>
      {isWidgetOpen && (
        <button
          type="button"
          aria-label="Close chat overlay"
          className="fixed inset-0 z-40 bg-neutral-950/40 backdrop-blur-[1px] transition-opacity md:bg-neutral-950/20"
          onClick={closeWidget}
        />
      )}

      <button
        id="fcw-toggle-btn"
        type="button"
        onClick={toggleWidget}
        className="fixed bottom-[calc(1rem+env(safe-area-inset-bottom))] right-[calc(1rem+env(safe-area-inset-right))] z-50 hidden h-11 min-w-0 items-center justify-center gap-2 rounded-full bg-white px-4 text-xs font-semibold text-neutral-900 shadow-[0_12px_30px_rgba(15,23,42,0.18)] ring-1 ring-neutral-200 transition-all hover:shadow-[0_16px_38px_rgba(15,23,42,0.24)] dark:bg-neutral-800 dark:text-neutral-100 dark:ring-neutral-700 md:flex"
        aria-label="Toggle chat"
      >
        <MessageCircle size={18} className="shrink-0 md:size-[15px]" />
        <span className="hidden sm:inline md:inline">Chat</span>

        {unreadTotal > 0 && (
          <span className="absolute -right-1 -top-1 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-rose-500 px-1.5 text-[10px] font-bold text-white shadow ring-2 ring-white dark:ring-neutral-800 md:static md:h-4 md:min-w-[16px] md:px-1 md:text-[9px] md:shadow-none md:ring-0">
            {unreadTotal > 99 ? "99+" : unreadTotal}
          </span>
        )}
      </button>

      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label="Chat"
        className={`fixed inset-x-0 bottom-0 z-50 flex h-[min(85dvh,42rem)] w-full overflow-hidden rounded-t-[1.75rem] border border-neutral-200 bg-white shadow-2xl transition-all duration-300 ease-out dark:border-neutral-800 dark:bg-neutral-900 md:left-auto md:right-[calc(1rem+env(safe-area-inset-right))] md:top-auto md:bottom-[calc(5.25rem+env(safe-area-inset-bottom))] md:h-[min(36rem,calc(100dvh-8.5rem))] md:w-[min(38rem,calc(100vw-2rem))] md:max-w-[38rem] md:rounded-[1.75rem] lg:h-[min(38rem,calc(100dvh-9.5rem))] lg:w-[min(44rem,calc(100vw-2.5rem))] lg:max-w-[44rem] ${
          isWidgetOpen
            ? "pointer-events-auto translate-y-0 opacity-100"
            : "pointer-events-none translate-y-6 opacity-0 md:translate-y-4"
        }`}
      >
        <div className="flex h-full w-full min-w-0 flex-col overflow-hidden">
          <div className="shrink-0 border-b border-white/10 bg-neutral-900 px-4 pb-3 pt-3 dark:bg-neutral-950 md:px-4 lg:px-5 lg:pt-4">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0 flex items-center gap-2.5">
                {mobileView === "chat" && activeConversation ? (
                  <>
                    <button
                      type="button"
                      onClick={() => setMobileView("list")}
                      className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-white/75 transition hover:bg-white/10 hover:text-white md:hidden"
                      aria-label="Back to conversation list"
                    >
                      <ChevronLeft size={18} />
                    </button>
                    <div className="flex min-w-0 items-center gap-2.5">
                      <SellerAvatar conversation={activeConversation} size="sm" />
                      <div className="min-w-0">
                        <span className="block truncate text-sm font-semibold text-white">
                          {getConversationDisplayName(activeConversation, "SHOPPER")}
                        </span>
                        <span className="block text-[11px] text-white/55">
                          {formatThreadTimestamp(activeConversation.last_message_at || activeConversation.created_at) || "Conversation"}
                        </span>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="flex items-center gap-2">
                    <MessageCircle size={16} className="shrink-0 text-white/70" />
                    <div>
                      <span className="block text-sm font-semibold uppercase tracking-[0.22em] text-white">
                        Messages
                      </span>
                      <span className="block text-[11px] text-white/55">
                        {loadingConvos ? `Refreshing conversations...` : `${conversations.length} conversation${conversations.length === 1 ? "" : "s"}`}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              <button
                type="button"
                onClick={closeWidget}
                className="shrink-0 rounded-full p-2 text-white/70 transition hover:bg-white/10 hover:text-white"
                aria-label="Close chat"
              >
                <X size={16} />
              </button>
            </div>
          </div>

          <div className="flex min-h-0 flex-1 overflow-hidden">
            <div className={`flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden border-neutral-100 dark:border-neutral-800 md:max-w-[17rem] md:shrink-0 md:border-r lg:max-w-[18.5rem] ${mobileView === "chat" ? "hidden md:flex" : "flex"}`}>
              <ConversationList
                conversations={conversations}
                activeConversationId={activeConversationId}
                loading={loadingConvos}
                onSelect={handleSelectConversation}
                actorType="SHOPPER"
                getConversationUnreadCount={getConversationUnreadCount}
                onSearch={searchConversations}
                emptyDescription="Start a conversation from a store, product, or cart to see it here."
              />
            </div>

            <div className={`min-h-0 min-w-0 flex-1 flex-col overflow-hidden ${mobileView === "chat" ? "flex" : "hidden md:flex"}`}>
              {!activeConversation ? (
                <div className="flex flex-1 flex-col items-center justify-center px-6 text-center text-neutral-400">
                    <div className="mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-neutral-100 text-neutral-300 dark:bg-neutral-800 dark:text-neutral-600">
                    <MessageCircle size={28} className="opacity-80" />
                  </div>
                  <p className="text-sm font-medium text-neutral-600 dark:text-neutral-300">No conversation selected</p>
                  <p className="mt-1 max-w-xs text-[12px] leading-relaxed opacity-80">
                    Pick a seller from the list to start chatting
                  </p>
                </div>
              ) : (
                <>
                  {interactionError ? (
                    <div className="shrink-0 border-b border-rose-200 bg-rose-50 px-3 py-2 text-[11px] text-rose-700 dark:border-rose-900/50 dark:bg-rose-900/20 dark:text-rose-200">
                      {interactionError}
                    </div>
                  ) : null}
                  <MessageList
                    messages={messages}
                    conversationKey={String(activeConversationId || "")}
                    actorType="SHOPPER"
                    loading={loadingMessages}
                    className="bg-neutral-50/70 dark:bg-neutral-950/20"
                  />
                  <MessageInput onSend={handleSend} sending={sending} disabled={!activeConversation || loadingMessages} />
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
